// Polls GET /projects/{id}/status for the project-open gate (R40). The caller binds the project id into fetchStatus.
import {ApiError, NoResponseError} from 'repository/errors';

const defaultSleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// API 3.2's state table: the states a project passes through before it settles as ready, rejected, error or archived.
export const IN_FLIGHT_STATES = new Set(['new', 'waiting_to_be_sent_to_tuner', 'waiting_for_identification', 'waiting_for_map_data']);

// DQ1's two conditions for opening a project: the v2 store holds it, and the pipeline has finished with it.
export const isConverted = status => status.storeVersion === 2;
export const hasSettled = status => !IN_FLIGHT_STATES.has(status.state);

// A poll that got no response, or a server error, is lost and the next may get through. A 401, 403 or 404 will not change by waiting.
const isLostPoll = error => error instanceof NoResponseError || (error instanceof ApiError && error.status >= 500);

/**
 * Calls fetchStatus() until predicate(status) holds, sleeping intervalMs between calls. It gives up, without sleeping, when the next poll
 * would begin at or after budgetMs from the first call (60 polls at the defaults). A lost poll counts as a poll with a null status.
 * Resolves {done, status, polls}, where status is the last poll's: null if that poll was lost.
 */
export const pollStatusUntil = async (fetchStatus, predicate, {intervalMs = 5000, budgetMs = 300000, sleep = defaultSleep, now = Date.now} = {}) => {
    const start = now();
    for (let polls = 1; ; polls += 1) {
        let status = null;
        try {
            status = await fetchStatus();
        } catch (error) {
            if (!isLostPoll(error)) {
                throw error;
            }
        }
        if (status !== null && predicate(status)) {
            return {done: true, status, polls};
        }
        if (now() + intervalMs >= start + budgetMs) {
            return {done: false, status, polls};
        }
        await sleep(intervalMs);
    }
};
